/**
 * Campaign Workspace — the home for an active campaign.
 *
 * Shows:
 *   - Campaign header (name, status, week X of Y, objective, confidence)
 *   - Metrics strip (Generated / Edited / Scheduled / Published / Shared)
 *   - 7-day asset list (each with platform/type/hook/body preview + status)
 *   - Inline editor for each asset (body + status toggle)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, CheckCircle, Clock, PencilSimple, PaperPlaneTilt,
  ChartLineUp, Lightning, ArrowRight, CaretDown
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const LIME = '#c8f060';

const STATUS_STYLE = {
  scheduled: { bg: 'rgba(200,240,96,0.08)',  fg: '#c8f060', border: 'rgba(200,240,96,0.2)',  label: 'Scheduled' },
  draft:     { bg: 'rgba(255,255,255,0.04)', fg: 'rgba(245,240,232,0.55)', border: 'rgba(255,255,255,0.08)', label: 'Draft' },
  published: { bg: 'rgba(100,210,255,0.08)', fg: '#64d2ff', border: 'rgba(100,210,255,0.2)', label: 'Published' },
  skipped:   { bg: 'rgba(255,154,118,0.08)', fg: '#ff9a76', border: 'rgba(255,154,118,0.2)', label: 'Skipped' },
  failed:    { bg: 'rgba(255,90,90,0.08)',   fg: '#ff5a5a', border: 'rgba(255,90,90,0.2)',   label: 'Failed' },
};

const Metric = ({ label, value, accent }) => (
  <div className="flex-1 min-w-[120px] p-4 rounded-xl" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
    <p className="text-[9px] tracking-[0.22em] uppercase font-bold mb-1.5" style={{ color: accent || 'rgba(245,240,232,0.45)' }}>{label}</p>
    <p className="text-3xl font-semibold tabular-nums" style={{ fontFamily: "'Instrument Serif', serif", color: '#fff' }}>{value}</p>
  </div>
);

const AssetRow = ({ asset, onSave }) => {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(asset.body || '');
  const [status, setStatus] = useState(asset.status || 'scheduled');
  const [saving, setSaving] = useState(false);
  const s = STATUS_STYLE[status] || STATUS_STYLE.scheduled;
  const dirty = body !== (asset.body || '') || status !== asset.status;

  const save = async () => {
    setSaving(true);
    const ok = await onSave(asset.id, { body, status });
    setSaving(false);
    if (ok) {
      toast.success('Asset saved.');
      setOpen(false);
    }
  };

  return (
    <div data-testid={`asset-row-${asset.day}`} className="rounded-2xl overflow-hidden" style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.06)' }}>
      <button onClick={() => setOpen(!open)} className="w-full grid grid-cols-[44px,90px,1fr,auto] gap-3 sm:gap-4 items-center px-4 py-3.5 text-left">
        <div className="text-center">
          <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: 'rgba(245,240,232,0.4)' }}>Day</p>
          <p className="text-xl font-semibold" style={{ color: LIME, fontFamily: "'Instrument Serif', serif" }}>{asset.day}</p>
        </div>
        <div>
          <p className="text-[12px] font-semibold" style={{ color: '#fff' }}>{asset.platform}</p>
          <p className="text-[10px] capitalize" style={{ color: 'rgba(245,240,232,0.45)' }}>{asset.content_type}</p>
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold truncate" style={{ color: '#fff' }}>{asset.hook}</p>
          <p className="text-[11px] truncate" style={{ color: 'rgba(245,240,232,0.5)' }}>{asset.angle}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>{s.label}</span>
          <CaretDown size={14} style={{ color: 'rgba(245,240,232,0.4)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s' }} />
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: 'hidden' }}>
            <div className="px-5 pb-5 pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="mb-3 grid grid-cols-2 gap-3 text-[11px]" style={{ color: 'rgba(245,240,232,0.55)' }}>
                <div>
                  <p className="text-[9px] tracking-[0.18em] uppercase mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>CTA</p>
                  <p>{asset.cta || '—'}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.18em] uppercase mb-1" style={{ color: 'rgba(245,240,232,0.4)' }}>Publish date</p>
                  <p style={{ color: LIME }}>{asset.publish_date}</p>
                </div>
              </div>
              <textarea
                data-testid={`asset-body-${asset.day}`}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={Math.min(20, Math.max(6, (body || '').split('\n').length + 2))}
                placeholder="Asset is generating…"
                className="w-full p-3 rounded-xl text-sm"
                style={{
                  background: '#0a0a0a', color: '#f5f0e8',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.65, resize: 'vertical',
                }}
              />
              <div className="flex gap-2 mt-3 flex-wrap">
                {['scheduled', 'draft', 'published', 'skipped'].map((st) => (
                  <button key={st} data-testid={`asset-status-${asset.day}-${st}`} onClick={() => setStatus(st)}
                    className="px-3 py-1.5 rounded-full text-[10px] font-semibold capitalize"
                    style={{
                      background: status === st ? STATUS_STYLE[st].bg : 'rgba(255,255,255,0.03)',
                      color: status === st ? STATUS_STYLE[st].fg : 'rgba(245,240,232,0.55)',
                      border: `1px solid ${status === st ? STATUS_STYLE[st].border : 'rgba(255,255,255,0.07)'}`,
                    }}>
                    {st}
                  </button>
                ))}
                <div className="flex-1" />
                <button data-testid={`asset-save-${asset.day}`} onClick={save} disabled={!dirty || saving}
                  className="px-4 py-1.5 rounded-full text-[12px] font-bold"
                  style={{
                    background: dirty ? LIME : 'rgba(255,255,255,0.05)',
                    color: dirty ? '#0a0a0a' : 'rgba(245,240,232,0.4)',
                    cursor: !dirty || saving ? 'not-allowed' : 'pointer',
                  }}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function CampaignWorkspacePage() {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/campaigns/${campaignId}`);
      setCampaign(data.campaign || null);
    } catch (e) {
      toast.error('Could not load campaign.');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const saveAsset = async (assetId, payload) => {
    try {
      await axios.post(`${API}/api/campaign-assets/${assetId}`, payload);
      await load();
      return true;
    } catch (e) {
      toast.error('Could not save.');
      return false;
    }
  };

  if (loading) {
    return <DashboardLayout><div className="py-32 text-center text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>Loading campaign…</div></DashboardLayout>;
  }
  if (!campaign) {
    return <DashboardLayout><div className="py-20 text-center"><p className="text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>Campaign not found.</p></div></DashboardLayout>;
  }

  const m = campaign.metrics || {};
  const confColor = (campaign.confidence_score || 0) >= 85 ? '#c8f060' : (campaign.confidence_score || 0) >= 75 ? '#f6c453' : '#ff9a76';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="campaign-workspace">
        {/* Back link */}
        <Link to={`/dashboard/strategy?id=${campaign.strategy_id}`} className="inline-flex items-center gap-2 text-xs font-semibold" style={{ color: 'rgba(245,240,232,0.45)' }}>
          <ArrowLeft size={14} /> Back to strategy deck
        </Link>

        {/* Header */}
        <div className="rounded-3xl p-6 sm:p-8" style={{ background: 'linear-gradient(180deg, #131313, #0c0c0c)', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid #c8f060' }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-[260px]">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: LIME }}>Active campaign</p>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold capitalize" style={{ background: 'rgba(200,240,96,0.1)', color: LIME, border: '1px solid rgba(200,240,96,0.2)' }}>
                  {campaign.status}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: 'rgba(245,240,232,0.5)' }}>· Week {campaign.week_number} of {campaign.total_weeks}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl mb-2" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 500, color: '#fff' }}>
                {campaign.name}
              </h1>
              <p className="text-sm italic" style={{ color: 'rgba(245,240,232,0.55)', fontFamily: "'Instrument Serif', serif" }}>
                {campaign.tagline}
              </p>
              <p className="text-xs mt-3 capitalize" style={{ color: 'rgba(245,240,232,0.5)' }}>
                Objective: <span style={{ color: LIME }}>{(campaign.objective || '').replace(/_/g, ' ')}</span>
              </p>
            </div>

            {/* Confidence */}
            {typeof campaign.confidence_score === 'number' && (
              <div className="text-right" style={{ minWidth: 200 }}>
                <p className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: 'rgba(245,240,232,0.5)' }}>Campaign confidence</p>
                <p className="text-5xl font-semibold tabular-nums" style={{ fontFamily: "'Instrument Serif', serif", color: confColor }}>{campaign.confidence_score}%</p>
                {(campaign.confidence_reasons || []).length > 0 && (
                  <ul className="text-[11px] mt-2 text-right space-y-0.5" style={{ color: 'rgba(245,240,232,0.55)' }}>
                    {(campaign.confidence_reasons || []).slice(0, 3).map((r, i) => (
                      <li key={i}>— {r}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Metrics strip */}
        <div className="flex gap-3 flex-wrap" data-testid="campaign-metrics">
          <Metric label="Generated" value={`${m.assets_generated || 0}/${m.assets_total || 0}`} accent={LIME} />
          <Metric label="Scheduled" value={m.assets_scheduled || 0} />
          <Metric label="Edited"    value={m.assets_edited || 0} />
          <Metric label="Published" value={m.assets_published || 0} accent="#64d2ff" />
          <Metric label="Shared"    value={m.assets_shared || 0} />
        </div>

        {/* Assets list */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: 'rgba(245,240,232,0.5)' }}>7-day asset queue</p>
            <Link to="/dashboard/calendar" className="text-[12px] font-semibold flex items-center gap-1" style={{ color: LIME }}>
              View on calendar <ArrowRight size={12} />
            </Link>
          </div>
          <div className="space-y-2.5">
            {(campaign.assets || []).map((a) => <AssetRow key={a.id} asset={a} onSave={saveAsset} />)}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
